/**
 * Cross-cutting hand-off for "ask the in-shell agent to do X".
 *
 * The embedded Shell runs an AI agent (Claude Code / Codex / Gemini), so the
 * reliable way to drive a one-off action (e.g. upgrading the `ano` CLI) is to
 * give the agent an instruction — the same mechanism onboarding uses — rather
 * than typing a raw command at a prompt that might be the agent's TUI or race
 * the agent launch (the bug class behind #1898).
 *
 * Two delivery paths, coordinated by a single-slot holder so they never
 * double-fire:
 *   - **Cold start** (no PTY yet): the shell-terminal lifecycle consumes the
 *     slot at spawn and bakes the instruction into the agent's launch prompt
 *     (`ANO_PENDING_AGENT_TASK` env → first user turn).
 *   - **Already-running agent**: the lifecycle's live `terminal:agent-task`
 *     listener consumes the slot and pastes the instruction into the agent.
 *
 * `consumePendingAgentTask()` clears the slot, so whichever path runs first
 * wins and the other no-ops. On a cold start the `terminal:agent-task` event
 * is dispatched before the listener mounts, so it's missed by design — the
 * spawn path picks the task up from the slot instead.
 */

/** Window event that nudges a live agent listener to deliver a queued task. */
export const AGENT_TASK_EVENT = "terminal:agent-task";

let pendingTask: string | null = null;

/**
 * Queue an instruction for the in-shell agent and open the Shell. Safe to
 * call regardless of whether the Shell is already open — see the module
 * docstring for how the two delivery paths coordinate.
 */
export function queueAgentTask(instruction: string): void {
  const trimmed = instruction.trim();
  if (!trimmed) return;
  pendingTask = trimmed;
  // Open (or focus) the Shell, then poke any live agent listener. A cold
  // start ignores the event (its listener isn't mounted yet) and the spawn
  // path picks the task up from the slot below.
  window.dispatchEvent(new CustomEvent("terminal:open"));
  window.dispatchEvent(new CustomEvent(AGENT_TASK_EVENT));
}

/** Read and clear the queued task. Returns null if none is pending. */
export function consumePendingAgentTask(): string | null {
  const task = pendingTask;
  pendingTask = null;
  return task;
}
